import { Box, Header, ScrollView, makeUclComponent } from '@rbilabs/universal-components';

import ActionButton from 'components/action-button';
import Picture from 'components/picture';
import SanityBlockRenderer from 'components/sanity-block-renderer';
import LoyaltyRewardsLogo from 'pages/loyalty/loyalty-rewards-logo';
import { getDesignToken } from 'styles/constants';

// `marginBottom` is measured (safe-area-derived) and set via the native `style` prop by the
// caller, not through this styled-system config — UCL's Box/ScrollView resolve numeric
// margin/padding props against the theme's space scale (e.g. 80 could resolve to the scale's
// `'80'` key instead of literal pixels), which silently corrupts runtime-computed values.
export const Container = ScrollView.withConfig({
  width: 'full',
});

export const HeadingText = Header.withConfig({
  accessibilityLevel: 1,
  margin: 0,
  color: getDesignToken('color-secondary'),
  fontSize: '5xl',
  lineHeight: '2xs',
  textAlign: 'center',
  textTransform: getDesignToken('text-transform-headlines'),
});

export const ImageContainer = Picture.withConfig<{ $windowHeight?: number }>(
  ({ $windowHeight }) => ({
    width: 'full',
    maxWidth: '80%',
    height: $windowHeight ? { base: 0.2 * $windowHeight, sm: 0.28 * $windowHeight } : 'auto',
    marginY: '$1',
    marginX: 'auto',
  })
);

export const TitleText = Header.withConfig<{ $fontColor?: string }>({
  variant: 'headerTwo',
  accessibilityLevel: 2,
  textTransform: getDesignToken('text-transform-headlines'),
  margin: 0,
  textAlign: 'center',
});

export const SubtitleText = Header.withConfig({
  variant: 'headerFour',
  accessibilityLevel: 4,
  width: '95%',
  margin: 0,
  marginTop: '$4',
  marginBottom: '$8',
  fontSize: 'md',
  fontFamily: 'body',
  textAlign: 'center',
});

export const DescriptionTextBlock = SanityBlockRenderer.withConfig({
  // Horizontal inset on the Box wrapper; Typography ignores NativeBase marginX/marginY.
  paddingX: '$4',
  textProps: {
    my: '$4',
    fontSize: 'md',
    fontFamily: 'body',
  },
  linkProps: {
    top: 0.5,
    underline: true,
  },
});

// Positions and reserves clearance above the floating tab bar. Kept free of border/shadow/
// background so that decoration (see ButtonCard) stays tight around the button instead of
// stretching across the reserved clearance below it.
// `paddingBottom` is measured (safe-area-derived) and set via the native `style` prop by the
// caller instead of here — see the Container comment above for why.
export const ButtonWrapper = Box.withConfig({
  width: 'full',
  position: 'absolute',
  bottom: 0,
});

export const ButtonCard = Box.withConfig<{ bottomInset?: number }>(({ bottomInset = 0 }) => ({
  marginTop: '$2',
  marginX: 'auto',
  alignItems: 'center',
  width: 'full',
  height: '$20',
  paddingX: '$4',
  justifyContent: 'center',
  borderTopColor: getDesignToken('border-color-default'),
  borderBottomColor: getDesignToken('border-color-default'),
  background: { base: getDesignToken('background-default'), sm: 'transparent' },
  // When floating above reserved tab bar clearance, the bottom border/shadow would render
  // in the middle of empty space instead of blending with the real screen edge — drop them.
  shadow: bottomInset ? 0 : { base: '2', sm: 0 },
  borderTopWidth: { base: '2', sm: 0 },
  borderBottomWidth: bottomInset ? 0 : { base: '2', sm: 0 },
}));

export const StyledLoyaltyLogo = LoyaltyRewardsLogo.withConfig({
  alignItems: 'center',
  marginTop: {
    base: '$8',
    lg: '$16',
  },
});

export const StyledActionButton = makeUclComponent(ActionButton).withConfig({
  paddingY: '$3',
  paddingX: '$12',
  _text: {
    fontSize: 'lg',
  },
  _web: {
    maxWidth: '480px',
  },
});

export const Terms = SanityBlockRenderer.withConfig({
  _web: {
    paddingY: '20px',
  },
  textProps: {
    textAlign: 'center',
    marginY: '$4',
    marginX: 'auto',
    fontSize: 'sm',
  },
} as any);
